/**
 * iter288 — ServiceCategoryPicker.
 *
 * Two-level picker for the dedicated "Offer a service" posting flow.
 * Fetches `/api/services/catalog` and renders a category → subcategory
 * selection with bilingual labels.
 *
 * Props:
 *   value:    { categoryId, subcategoryId }
 *   onChange: (sel) => void   // sel: {categoryId, subcategoryId} | null
 *   isArabic: boolean
 */
import React, { useState, useEffect } from 'react';
import { getApiBase } from '../api/httpClient';
import './ServiceCategoryPicker.css';

export default function ServiceCategoryPicker({ value, onChange, isArabic }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBase()}/api/services/catalog`)
      .then(r => r.json())
      .then(d => { if (!cancelled) { setCatalog(d.categories || []); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selectedCat = catalog.find(c => c.id === value?.categoryId);
  const selectedSub = selectedCat?.subcategories.find(s => s.id === value?.subcategoryId);

  const handleCategoryPick = (cat) => {
    onChange({ categoryId: cat.id, subcategoryId: null });
  };

  const handleSubPick = (sub) => {
    onChange({ categoryId: selectedCat.id, subcategoryId: sub.id });
  };

  const handleReset = () => {
    onChange(null);
  };

  if (loading) {
    return (
      <div className="service-picker-loading" data-testid="service-picker-loading">
        {isArabic ? 'جاري تحميل فئات الخدمات…' : 'Loading service categories…'}
      </div>
    );
  }

  // Step A: pick a top-level category.
  if (!selectedCat) {
    return (
      <div className="service-picker" data-testid="service-picker">
        <div className="service-picker-header">
          <span className="service-picker-title">
            {isArabic ? 'اختر فئة الخدمة' : 'Choose a service category'}
          </span>
          {/* iter289 — Helper text explaining the count badge on each
              card. Operator-spec requirement so users know the number
              represents service types inside the category. */}
          <span className="service-picker-helper">
            {isArabic
              ? 'الرقم يوضح عدد أنواع الخدمات داخل كل فئة.'
              : 'Number shows available service types inside each category.'}
          </span>
        </div>
        <div className="service-picker-grid">
          {catalog.map(cat => (
            <button
              key={cat.id}
              type="button"
              className="service-picker-card"
              onClick={() => handleCategoryPick(cat)}
              data-testid={`service-cat-${cat.id}`}
            >
              <span className="service-picker-card-title">
                {isArabic ? cat.ar : cat.en}
              </span>
              <span className="service-picker-card-count">
                {/* iter289 — Replace bare number with the full phrase so
                    users know what the count refers to. Bilingual. */}
                {isArabic
                  ? `${cat.subcategories.length} أنواع خدمات`
                  : `${cat.subcategories.length} service types`}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Step B: pick a subcategory within the chosen category.
  return (
    <div className="service-picker service-picker-step-b" data-testid="service-picker-step-b">
      <div className="service-picker-header">
        <button
          type="button"
          className="service-picker-back"
          onClick={handleReset}
          data-testid="service-picker-back"
        >
          {isArabic ? '← الرجوع للفئات' : '← Back to categories'}
        </button>
        <span className="service-picker-title">
          {isArabic ? selectedCat.ar : selectedCat.en}
        </span>
      </div>

      {/* iter288 — Health & Care safety note. Operator spec required
          this surfaces here so users see it before publishing. */}
      {(selectedCat.safety_note_ar || selectedCat.safety_note_en) && (
        <div className="service-picker-safety" data-testid="service-picker-safety-note">
          {isArabic ? selectedCat.safety_note_ar : selectedCat.safety_note_en}
        </div>
      )}

      <div className="service-picker-list" data-testid="service-picker-subcat-list">
        {selectedCat.subcategories.map(sub => {
          const isActive = selectedSub?.id === sub.id;
          return (
            <button
              key={sub.id}
              type="button"
              className={`service-picker-list-row ${isActive ? 'active' : ''}`}
              onClick={() => handleSubPick(sub)}
              data-testid={`service-sub-${sub.id}`}
              aria-pressed={isActive}
            >
              {/* iter289 — Premium row layout: bullet indicator dot on
                  the left, sub label, then an animated check on the
                  right for the selected state. */}
              <span className="service-picker-list-row-bullet" aria-hidden="true" />
              <span className="service-picker-list-row-label">
                {isArabic ? sub.ar : sub.en}
              </span>
              {isActive ? (
                <span className="service-picker-check" aria-hidden="true">✓</span>
              ) : (
                <span className="service-picker-chev" aria-hidden="true">›</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
